// Onboarding funnel event definitions.
//
// The funnel is measurable from these four events alone: `viewed` marks entry
// to a stage, `completed` marks advancing past it, so drop-off at stage N is
// "viewed N without completed N". `onboarding_completed` fires on every path
// that finishes the flow, and `onboarding_exited` means abandonment only.

import type { EventDefinition } from './navigation';

/** The six stages, in order. `simulation` is the interactive explainer. */
export type OnboardingStageName =
  | 'connect_socials'
  | 'profile'
  | 'interest_topics'
  | 'interest_platforms'
  | 'claim_devnet'
  | 'simulation';

export type OnboardingStartedProperties = {
  source: 'new_user';
};

export type OnboardingStageViewedProperties = {
  stage: OnboardingStageName;
  /** 1-based position, so a funnel can order stages without a name lookup. */
  index: number;
  /** True when the stage was reached by resuming a persisted session rather
   *  than by advancing through the flow in this sitting. */
  resumed: boolean;
};

export type OnboardingStageCompletedProperties = {
  stage: OnboardingStageName;
  index: number;
  durationMs: number;
  /** Only meaningful on `simulation`, the one stage that can be skipped while
   *  still completing onboarding. Omitted elsewhere. */
  skipped?: boolean;
};

export type OnboardingCompletedProperties = {
  totalDurationMs: number;
  /** Finished the explainer, or pressed Skip on it. Both complete the flow. */
  skippedExplainer: boolean;
};

export type OnboardingExitedProperties = {
  /** Abandonment only — leaving via the exit button without finishing. */
  exitStage: OnboardingStageName;
  index: number;
  subScreen: string;
};

export const ONBOARDING_STARTED: EventDefinition<OnboardingStartedProperties> = {
  name: 'onboarding_started',
};

export const ONBOARDING_STAGE_VIEWED: EventDefinition<OnboardingStageViewedProperties> = {
  name: 'onboarding_stage_viewed',
};

export const ONBOARDING_STAGE_COMPLETED: EventDefinition<OnboardingStageCompletedProperties> = {
  name: 'onboarding_stage_completed',
};

export const ONBOARDING_COMPLETED: EventDefinition<OnboardingCompletedProperties> = {
  name: 'onboarding_completed',
};

export const ONBOARDING_EXITED: EventDefinition<OnboardingExitedProperties> = {
  name: 'onboarding_exited',
};
